<script setup>
import { ref, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SettingsDocumentUploadForm from '@/Components/Forms/SettingsDocumentUploadForm.vue'

const props = defineProps({
  user: Object,
})

const documents = ref({
  dbs_document: null,
  identity_document: null
})

const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Populate documents from user prop
onMounted(() => {
  if (props.user?.documents) {
    const dbsDoc = props.user.documents.find(doc => doc.type === 'dbs')
    const identityDoc = props.user.documents.find(doc => doc.type === 'identity')

    documents.value = {
      dbs_document: dbsDoc ? {
        path: dbsDoc.path,
        original_name: dbsDoc.original_name,
        size: dbsDoc.size,
        type: 'dbs'
      } : null,
      identity_document: identityDoc ? {
        path: identityDoc.path,
        original_name: identityDoc.original_name,
        size: identityDoc.size,
        type: 'identity'
      } : null
    }
  }
})

const updateDocuments = (newDocuments) => {
  documents.value = newDocuments
}

const saveDocuments = async () => {
  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/documents', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(documents.value)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Map Laravel errors (arrays) to flat error messages (strings)
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Documents updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- Document Upload Form -->
      <SettingsDocumentUploadForm
        :model-value="documents"
        @update:model-value="updateDocuments"
        :errors="errors"
        title="Verification Documents"
        description="Manage your verification documents. These documents are used to verify your identity and qualifications."
        :show-shadow="false"
      />

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveDocuments"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
